import { AuthSessionBoundary } from '@/components/auth-session-boundary'
import { ErrorBoundary } from '@/components/error-boundary'
import { AccessDenied } from '@/components/layout/access-denied'
import { AccessGuard } from '@/components/layout/access-guard'
import { AdminLayout } from '@/components/layout/admin-layout'
import { AuthorizationUnavailable } from '@/components/layout/authorization-unavailable'
import { CoachLayout } from '@/components/layout/coach-layout'
import { CoachManagerLayout } from '@/components/layout/coach-manager-layout'
import { LayoutSelector } from '@/components/layout/layout-selector'
import { MainLayout } from '@/components/layout/main-layout'
import { PortalLayout } from '@/components/layout/portal-layout'
import { ProtectedAdminRoute } from '@/components/layout/protected-admin-route'
import { ProtectedCoachManagerRoute } from '@/components/layout/protected-coach-manager-route'
import { ProtectedCoachRoute } from '@/components/layout/protected-coach-route'
import { ProtectedPortalRoute } from '@/components/layout/protected-portal-route'
import { ProtectedRoute } from '@/components/layout/protected-route'
import { ResponsiveLayout } from '@/components/layout/responsive-layout'
import { SupportStandaloneLayout } from '@/components/layout/support-standalone-layout'
import { LenisProvider } from '@/components/lenis-provider'
import { type ParticipantRouteAccessId, canAccessParticipantRoute } from '@/config/route-access'
import { useAccessControl } from '@/hooks/use-access-control'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useSessionState } from '@/hooks/use-session-state'
import { firstAllowedParticipantRoute } from '@/lib/access-policy'
import {
  enableAiInterview,
  enableBenefits,
  enableCoachRoster,
  enableCoaching,
  enableCostSavings,
  enableLearn,
  enableSkillsMap,
  enableSupport,
} from '@/lib/feature-flags'
// SidebarLayout kept for reference but no longer used in embrace routes
// import { SidebarLayout } from '@/components/layout/sidebar-layout'
// Dashboard redirects to /resume in new layout
import { useAuthStore } from '@/stores/auth.store'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Loader } from 'lucide-react'
import { type ComponentType, type ReactNode, Suspense, lazy } from 'react'
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import type { SessionState } from './types'

function RouteLoadingFallback() {
  return (
    <output
      className="flex min-h-[40vh] items-center justify-center gap-3 text-sm text-muted-foreground"
      aria-live="polite"
    >
      <Loader className="h-5 w-5 animate-spin text-status" aria-hidden="true" />
      <span>Loading page…</span>
    </output>
  )
}

// Keep the initial bundle focused on the current route. The wrapper gives every
// deferred page the same accessible loading UI without changing route behavior.
function lazyNamedPage(loader: () => Promise<unknown>, exportName: string) {
  const Page = lazy(async () => {
    const pageModule = (await loader()) as Record<string, ComponentType>
    const page = pageModule[exportName]
    if (!page) {
      throw new Error(`Page module does not export ${exportName}`)
    }
    return { default: page }
  })

  function LazyPage() {
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Page />
      </Suspense>
    )
  }

  LazyPage.displayName = `Lazy${exportName}`
  return LazyPage
}

const adminPages = () => import('@/pages/admin')
const portalPages = () => import('@/pages/portal')
const coachPages = () => import('@/pages/coach-portal')
const coachManagerPages = () => import('@/pages/coach-manager')

const AdminAnalyticsPage = lazyNamedPage(adminPages, 'AdminAnalyticsPage')
const AdminBooksPage = lazyNamedPage(adminPages, 'AdminBooksPage')
const AdminCareerOnboardingPage = lazyNamedPage(adminPages, 'AdminCareerOnboardingPage')
const AdminCoachVettingPage = lazyNamedPage(adminPages, 'AdminCoachVettingPage')
const AdminCrisisAlertsPage = lazyNamedPage(adminPages, 'AdminCrisisAlertsPage')
const AdminDLQPage = lazyNamedPage(adminPages, 'AdminDLQPage')
const AdminDashboardPage = lazyNamedPage(adminPages, 'AdminDashboardPage')
const AdminImportPage = lazyNamedPage(adminPages, 'AdminImportPage')
const AdminObservabilityPage = lazyNamedPage(adminPages, 'AdminObservabilityPage')
const AdminOrganizationsPage = lazyNamedPage(adminPages, 'AdminOrganizationsPage')
const AdminPodcastsPage = lazyNamedPage(adminPages, 'AdminPodcastsPage')
const AdminReportingPage = lazyNamedPage(adminPages, 'AdminReportingPage')
const AdminResourcesPage = lazyNamedPage(adminPages, 'AdminResourcesPage')
const AdminUserFormPage = lazyNamedPage(adminPages, 'AdminUserFormPage')
const AdminUsersPage = lazyNamedPage(adminPages, 'AdminUsersPage')
const JobCorpusPage = lazyNamedPage(adminPages, 'JobCorpusPage')

const PortalBenefitsPage = lazyNamedPage(portalPages, 'PortalBenefitsPage')
const PortalDashboardPage = lazyNamedPage(portalPages, 'PortalDashboardPage')
const PortalEmployeesPage = lazyNamedPage(portalPages, 'PortalEmployeesPage')
const PortalHrisConnectionPage = lazyNamedPage(portalPages, 'PortalHrisConnectionPage')
const PortalExitGuideDocumentsPage = lazyNamedPage(portalPages, 'PortalExitGuideDocumentsPage')
const PortalImportHistoryPage = lazyNamedPage(portalPages, 'PortalImportHistoryPage')
const PortalImportPage = lazyNamedPage(portalPages, 'PortalImportPage')
const PortalInternalJobsPage = lazyNamedPage(portalPages, 'PortalInternalJobsPage')
const PortalInternalMobilityDepartmentNeedsPage = lazyNamedPage(
  portalPages,
  'PortalInternalMobilityDepartmentNeedsPage',
)
const PortalInternalMobilityDepartmentDetailPage = lazyNamedPage(
  portalPages,
  'PortalInternalMobilityDepartmentDetailPage',
)
const PortalInternalMobilityCandidatesPage = lazyNamedPage(
  portalPages,
  'PortalInternalMobilityCandidatesPage',
)
const PortalInternalMobilityMatrixPage = lazyNamedPage(
  portalPages,
  'PortalInternalMobilityMatrixPage',
)
const PortalInternalMobilityOrganizationChartPage = lazyNamedPage(
  portalPages,
  'PortalInternalMobilityOrganizationChartPage',
)
const PortalLoginPage = lazyNamedPage(portalPages, 'PortalLoginPage')
const PortalMatchReportsPage = lazyNamedPage(portalPages, 'PortalMatchReportsPage')
const PortalRoiPage = lazyNamedPage(portalPages, 'PortalRoiPage')
const PortalSeveranceImportPage = lazyNamedPage(portalPages, 'PortalSeveranceImportPage')
const PortalStakeholderContactsPage = lazyNamedPage(portalPages, 'PortalStakeholderContactsPage')

const CoachAvailabilityPage = lazyNamedPage(coachPages, 'CoachAvailabilityPage')
const CoachDashboardPage = lazyNamedPage(coachPages, 'CoachDashboardPage')
const CoachClientsPage = lazyNamedPage(coachPages, 'CoachClientsPage')
const CoachLoginPage = lazyNamedPage(coachPages, 'CoachLoginPage')
const CoachProfilePage = lazyNamedPage(coachPages, 'CoachProfilePage')

const CoachManagerCandidatesPage = lazyNamedPage(coachManagerPages, 'CoachManagerCandidatesPage')
const CoachManagerChatSafeguardsPage = lazyNamedPage(
  coachManagerPages,
  'CoachManagerChatSafeguardsPage',
)
const CoachManagerCoachesPage = lazyNamedPage(coachManagerPages, 'CoachManagerCoachesPage')
const CoachManagerLoginPage = lazyNamedPage(coachManagerPages, 'CoachManagerLoginPage')
const CoachManagerMatchingPage = lazyNamedPage(coachManagerPages, 'CoachManagerMatchingPage')
const CoachManagerOperationsPage = lazyNamedPage(coachManagerPages, 'CoachManagerOperationsPage')
const CoachManagerReviewPage = lazyNamedPage(coachManagerPages, 'CoachManagerReviewPage')
const CoachManagerReviewsPage = lazyNamedPage(coachManagerPages, 'CoachManagerReviewsPage')

const AgentPage = lazyNamedPage(() => import('@/pages/agent'), 'AgentPage')
// Superseded on /benefits/election by SeveranceGuidedPage (below). Kept in
// the tree and still routed, at /benefits/election-legacy -- same treatment
// /benefits-legacy got when the Exit Guide took over /benefits.
const BenefitsElectionLegacyPage = lazyNamedPage(
  () => import('@/pages/benefits/benefits-election'),
  'BenefitsElectionPage',
)
const SeveranceGuidedPage = lazyNamedPage(
  () => import('@/pages/benefits/severance-guided'),
  'SeveranceGuidedPage',
)
const BenefitsSignupPage = lazyNamedPage(
  () => import('@/pages/benefits/benefits-signup'),
  'BenefitsSignupPage',
)
const ChatPage = lazyNamedPage(() => import('@/pages/chat'), 'ChatPage')
const HomePage = lazyNamedPage(() => import('@/pages/home'), 'HomePage')
const CareerOnboardingPage = lazyNamedPage(
  () => import('@/pages/career-onboarding'),
  'CareerOnboardingPage',
)
const CoachConversationsPage = lazyNamedPage(
  () => import('@/pages/coach-conversations'),
  'CoachConversationsPage',
)
const CoachingLivePage = lazyNamedPage(() => import('@/pages/coaching-live'), 'CoachingLivePage')
const CoachRosterPage = lazyNamedPage(() => import('@/pages/coach-roster'), 'CoachRosterPage')
const ConnectPage = lazyNamedPage(() => import('@/pages/connect'), 'ConnectPage')
const ContactPage = lazyNamedPage(() => import('@/pages/contact'), 'ContactPage')
const InterviewHistoryPage = lazyNamedPage(
  () => import('@/pages/interview-history'),
  'InterviewHistoryPage',
)
const InterviewNewPage = lazyNamedPage(() => import('@/pages/interview-new'), 'InterviewNewPage')
const InterviewSessionPage = lazyNamedPage(
  () => import('@/pages/interview-session'),
  'InterviewSessionPage',
)
const LandingPage = lazyNamedPage(() => import('@/pages/landing'), 'LandingPage')
const LearnPage = lazyNamedPage(() => import('@/pages/learn'), 'LearnPage')
const LearnBookPage = lazyNamedPage(() => import('@/pages/learn-book'), 'LearnBookPage')
const LearnBooksPage = lazyNamedPage(() => import('@/pages/learn-books'), 'LearnBooksPage')
const LearnModulePage = lazyNamedPage(() => import('@/pages/learn-module'), 'LearnModulePage')
const LearnOnboardPage = lazyNamedPage(() => import('@/pages/learn-onboard'), 'LearnOnboardPage')
const LearnProfilePage = lazyNamedPage(() => import('@/pages/learn-profile'), 'LearnProfilePage')
const LearnResourcesPage = lazyNamedPage(
  () => import('@/pages/learn-resources'),
  'LearnResourcesPage',
)
const ResourceLibraryPage = lazyNamedPage(
  () => import('@/pages/resource-library'),
  'ResourceLibraryPage',
)
const LearnStatsPage = lazyNamedPage(() => import('@/pages/learn-stats'), 'LearnStatsPage')
const LearnTopicPage = lazyNamedPage(() => import('@/pages/learn-topic'), 'LearnTopicPage')
const LoginPage = lazyNamedPage(() => import('@/pages/login'), 'LoginPage')
const NewsIndexPage = lazyNamedPage(() => import('@/pages/news/news-index'), 'NewsIndexPage')
const NewsArticlePage = lazyNamedPage(() => import('@/pages/news/news-article'), 'NewsArticlePage')
const NotFoundPage = lazyNamedPage(() => import('@/pages/not-found'), 'NotFoundPage')
const ExitGuidePage = lazyNamedPage(() => import('@/pages/exit-guide/exit-guide'), 'ExitGuidePage')
const ExitGuideDocumentPage = lazyNamedPage(
  () => import('@/pages/exit-guide/exit-guide-document'),
  'ExitGuideDocumentPage',
)
const OpportunitiesPage = lazyNamedPage(() => import('@/pages/opportunities'), 'OpportunitiesPage')
const PrivacyPolicyPage = lazyNamedPage(() => import('@/pages/privacy'), 'PrivacyPolicyPage')
const ProfilePage = lazyNamedPage(() => import('@/pages/profile'), 'ProfilePage')
const ResumePage = lazyNamedPage(() => import('@/pages/resume'), 'ResumePage')
const ResumeBuilderPage = lazyNamedPage(() => import('@/pages/resume-builder'), 'ResumeBuilderPage')
const ResumeBuilderPocPage = lazyNamedPage(
  () => import('@/pages/resume-builder-poc'),
  'ResumeBuilderPocPage',
)
const SeverancePage = lazyNamedPage(() => import('@/lab/severance'), 'SeverancePage')
const SkillsMapPage = lazyNamedPage(
  () => import('@/pages/skills-map-experience'),
  'SkillsMapExperiencePage',
)
const SMSOptInExamplePage = lazyNamedPage(
  () => import('@/pages/sms-opt-in-example'),
  'SMSOptInExamplePage',
)
const StatusPage = lazyNamedPage(() => import('@/pages/status'), 'StatusPage')
const SupportPage = lazyNamedPage(() => import('@/pages/support/support'), 'SupportPage')
const SupportTopicPage = lazyNamedPage(
  () => import('@/pages/support/support-topic'),
  'SupportTopicPage',
)
const TermsOfServicePage = lazyNamedPage(
  () => import('@/pages/terms-of-service'),
  'TermsOfServicePage',
)
const TrackerPage = lazyNamedPage(() => import('@/pages/tracker'), 'TrackerPage')
const UpdatesPage = lazyNamedPage(() => import('@/pages/updates'), 'UpdatesPage')
const WhitepaperLandingPage = lazyNamedPage(
  () => import('@/pages/whitepaper'),
  'WhitepaperLandingPage',
)
const WhitepaperDownloadPage = lazyNamedPage(
  () => import('@/pages/whitepaper-download'),
  'WhitepaperDownloadPage',
)
const WhitepapersIndexPage = lazyNamedPage(
  () => import('@/pages/whitepapers-index'),
  'WhitepapersIndexPage',
)
const SeveranceAdministrationPlaybookPage = lazyNamedPage(
  () => import('@/pages/severance-administration-playbook'),
  'SeveranceAdministrationPlaybookPage',
)
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then(({ ReactQueryDevtools: Devtools }) => ({
    default: Devtools,
  })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// VITE_SITE determines which site we're building for:
// - "pelgo" (pelgo.com): shows landing page at /
// - "embrace" (embrace.pelgo.com): redirects / to login/dashboard
// - "console" (console.pelgo.com): admin dashboard
// - "portal" (portal.pelgo.com): HR portal
// - "coach" (coach.pelgo.com): Coach Portal
// - "coach-manager" (coach-manager.pelgo.com): Coach Manager Portal (coach
//   matching/assignment ops, distinct from the general admin console)
const envSite: string = import.meta.env.VITE_SITE || 'embrace'
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const site: string = hostname === 'status.pelgo.com' ? 'status' : envSite

// Web: session-state API drives resume / opportunities. Mobile: /chat vs /opportunities from resume + prefs.
export function getRootRedirectTarget(
  isAuthenticated: boolean,
  sessionState: SessionState | null | undefined,
  hasSessionError: boolean,
  locationSearch: string,
): string {
  if (!isAuthenticated) {
    return `/login${locationSearch}`
  }

  if (sessionState && !sessionState.learning_goal) {
    return `/login${locationSearch}`
  }

  if (sessionState?.learning_goal === 'level_up') {
    return enableLearn ? '/learn' : '/resume'
  }

  if (hasSessionError || !sessionState) {
    return '/resume'
  }

  switch (sessionState.route) {
    case 'career_onboarding':
      return '/onboarding/career'
    case 'onboarding':
      return '/resume'
    case 'job_preferences':
      return '/resume'
    case 'opportunities':
      // Onboarding is done, so the summary is more useful than any single
      // feature tab (PEL-1030).
      return '/home'
  }
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const authorization = useAccessControl(isAuthenticated)
  const isMobile = useIsMobile()
  const location = useLocation()
  const { data: sessionState, isLoading, isError } = useSessionState()

  if (!isAuthenticated) {
    return <Navigate to={`/login${location.search}`} replace />
  }

  if (authorization.isPending) return null
  if (authorization.isError || !authorization.data) {
    return <AuthorizationUnavailable retry={() => void authorization.refetch()} />
  }

  const participantLanding = firstAllowedParticipantRoute(authorization.data)
  if (!participantLanding) {
    return (
      <AccessDenied
        title="Participant program unavailable"
        message="Your account is signed in, but no participant journey is configured for it."
      />
    )
  }
  if (participantLanding !== '/resume') {
    return <Navigate to={participantLanding} replace />
  }

  if (!isMobile) {
    if (isLoading) {
      return null
    }
    const target = getRootRedirectTarget(isAuthenticated, sessionState, isError, location.search)
    const routeId =
      target === '/learn'
        ? 'participant.learn'
        : target === '/home'
          ? 'participant.home'
          : 'participant.external-jobs'
    return (
      <Navigate
        to={canAccessParticipantRoute(authorization.data, routeId) ? target : participantLanding}
        replace
      />
    )
  }

  return <MobileRedirect />
}

function participantElement(routeId: ParticipantRouteAccessId, element: ReactNode) {
  return <AccessGuard routeId={routeId}>{element}</AccessGuard>
}

function LegacyCoachRosterRedirect() {
  const [searchParams] = useSearchParams()
  const destination =
    searchParams.get('track') === 'business'
      ? '/start-a-business'
      : searchParams.get('track') === 'retirement'
        ? '/retirement'
        : '/coaching'
  return <Navigate to={destination} replace />
}

function MobileRedirect() {
  const { data: sessionState, isLoading } = useSessionState()

  if (isLoading)
    return (
      <div className="h-dvh flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-status" />
      </div>
    )

  if (!sessionState || !sessionState.learning_goal) {
    return <Navigate to="/login" replace />
  }

  if (sessionState.has_resume && sessionState.has_preferences) {
    return <Navigate to="/home" replace />
  }

  return <Navigate to="/chat" replace />
}

// Console (admin) routes
const consoleRouter = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <MainLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        element: <ProtectedAdminRoute />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                path: '/dashboard',
                element: <AdminDashboardPage />,
              },
              {
                path: '/users',
                element: (
                  <AccessGuard routeId="console.users">
                    <AdminUsersPage />
                  </AccessGuard>
                ),
              },
              {
                path: '/career-onboarding',
                element: (
                  <AccessGuard routeId="console.users">
                    <AdminCareerOnboardingPage />
                  </AccessGuard>
                ),
              },
              {
                path: '/coach-review',
                element: <AdminCoachVettingPage />,
              },
              {
                path: '/crisis-alerts',
                element: <AdminCrisisAlertsPage />,
              },
              {
                path: '/users/new',
                element: (
                  <AccessGuard routeId="console.users">
                    <AdminUserFormPage />
                  </AccessGuard>
                ),
              },
              {
                path: '/users/:id',
                element: (
                  <AccessGuard routeId="console.users">
                    <AdminUserFormPage />
                  </AccessGuard>
                ),
              },
              {
                path: '/import',
                element: <AdminImportPage />,
              },
              {
                path: '/user-analytics',
                element: <AdminAnalyticsPage />,
              },
              {
                path: '/job-corpus',
                element: <JobCorpusPage />,
              },
              {
                path: '/organizations',
                element: <AdminOrganizationsPage />,
              },
              {
                path: '/podcasts',
                element: <AdminPodcastsPage />,
              },
              {
                path: '/books',
                element: <AdminBooksPage />,
              },
              {
                path: '/resources',
                element: <AdminResourcesPage />,
              },
              {
                path: '/dlq',
                element: <AdminDLQPage />,
              },
              {
                path: '/observability',
                element: <AdminObservabilityPage />,
              },
              {
                path: '/analytics',
                element: <AdminReportingPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  // Legal pages
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
])

function LegacySupportTopicRedirect() {
  const { topicId } = useParams()
  // /benefits now belongs to the new Exit Guide-backed page (2026-09-02) --
  // the old static Benefits/Support pages moved to /benefits-legacy so
  // this legacy alias still lands somewhere real instead of the new page.
  return <Navigate to={topicId ? `/benefits-legacy/${topicId}` : '/benefits-legacy'} replace />
}

function LegacySkillsMapRedirect() {
  const location = useLocation()
  return <Navigate to={`/career-map${location.search}${location.hash}`} replace />
}

// Embrace (app) routes
export const embraceRouter = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/onboarding/career',
        element: <CareerOnboardingPage />,
      },
    ],
  },
  // Dev-only scenario demo for resume-builder scoping (no auth, standalone).
  ...(import.meta.env.DEV
    ? [
        {
          path: '/resume-builder-poc',
          element: <ResumeBuilderPocPage />,
          errorElement: <ErrorBoundary />,
        },
      ]
    : []),
  {
    element: <ResponsiveLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/welcome',
            element: participantElement(
              'participant.external-jobs',
              <Navigate to="/resume" state={{ fromWelcome: true }} replace />,
            ),
          }, // legacy URL; welcome UI lives on /login
          {
            element: <LayoutSelector />,
            children: [
              {
                path: '/home',
                element: participantElement('participant.home', <HomePage />),
              },
              {
                path: '/dashboard',
                element: participantElement('participant.home', <Navigate to="/home" replace />),
              },
              {
                path: '/chat',
                element: participantElement('participant.jobs', <ChatPage />),
              },
              ...(enableCoaching
                ? [
                    {
                      path: '/coaching',
                      element: participantElement('participant.coaching', <CoachingLivePage />),
                    },
                    {
                      path: '/coaching/messages',
                      element: participantElement(
                        'participant.coaching',
                        <CoachConversationsPage />,
                      ),
                    },
                  ]
                : []),
              ...(enableCoaching && enableCoachRoster
                ? [
                    {
                      path: '/start-a-business',
                      element: participantElement(
                        'participant.start-business',
                        <CoachRosterPage />,
                      ),
                    },
                    {
                      path: '/retirement',
                      element: participantElement('participant.retirement', <CoachRosterPage />),
                    },
                    {
                      path: '/coaching/roster',
                      element: participantElement(
                        'participant.coaching',
                        <LegacyCoachRosterRedirect />,
                      ),
                    },
                  ]
                : []),
              {
                path: '/resume',
                element: participantElement('participant.external-jobs', <ResumePage />),
              },
              {
                path: '/resume-builder',
                element: participantElement('participant.jobs', <ResumeBuilderPage />),
              },
              {
                path: '/agent',
                element: participantElement('participant.jobs', <AgentPage />),
              },
              ...(enableSkillsMap
                ? [
                    {
                      path: '/career-map',
                      element: participantElement('participant.skills-map', <SkillsMapPage />),
                    },
                    {
                      path: '/skills-map',
                      element: participantElement(
                        'participant.skills-map',
                        <LegacySkillsMapRedirect />,
                      ),
                    },
                  ]
                : []),
              // A restricted participant must always have a route that can clear the
              // server-side hard gate. VITE_ENABLE_BENEFITS controls the optional
              // public claim/admin surfaces, not this authenticated recovery path.
              {
                path: '/benefits/election',
                element: participantElement('participant.election', <SeveranceGuidedPage />),
              },
              {
                // The binary lump-sum/continuation election this path used to
                // serve. Not deleted -- it is the only surface that writes a
                // real election, and the guided flow above is still a mock.
                path: '/benefits/election-legacy',
                element: participantElement('participant.election', <BenefitsElectionLegacyPage />),
              },
              {
                // Moved off /benefits 2026-09-02 -- that path now belongs
                // to the new Exit Guide-backed page (see the /exit-guide
                // registration below, and app-layout.tsx's consolidated
                // "Benefits" nav item). Kept live (not deleted) per
                // instruction -- just no longer linked from the sidebar.
                path: '/benefits-legacy',
                element: participantElement('participant.benefits', <SupportPage />),
              },
              {
                path: '/support',
                element: participantElement(
                  'participant.benefits',
                  <Navigate to="/benefits-legacy" replace />,
                ),
              },
              {
                path: '/connect',
                element: participantElement('participant.jobs', <ConnectPage />),
              },
              {
                path: '/opportunities',
                element: participantElement('participant.jobs', <OpportunitiesPage />),
              },
              {
                path: '/tracker',
                element: participantElement('participant.external-jobs', <TrackerPage />),
              },
              ...(enableAiInterview
                ? [
                    {
                      path: '/interview',
                      element: participantElement('participant.jobs', <InterviewHistoryPage />),
                    },
                    {
                      path: '/interview/new',
                      element: participantElement('participant.jobs', <InterviewNewPage />),
                    },
                    {
                      path: '/interview/:id',
                      element: participantElement('participant.jobs', <InterviewSessionPage />),
                    },
                  ]
                : []),
              {
                path: '/profile',
                element: participantElement('participant.profile', <ProfilePage />),
              },
              {
                // Renamed from /exit-guide 2026-09-02 -- this is now the
                // "Benefits" hub the sidebar links to (see app-layout.tsx),
                // taking over the path the old static Benefits/Support
                // pages used before they moved to /benefits-legacy.
                path: '/benefits',
                element: participantElement('participant.benefits', <ExitGuidePage />),
              },
              {
                path: '/benefits/:documentId',
                element: participantElement('participant.benefits', <ExitGuideDocumentPage />),
              },
              {
                path: '/resource-library',
                element: participantElement(
                  'participant.resource-library',
                  <ResourceLibraryPage />,
                ),
              },
              ...(enableLearn
                ? [
                    {
                      path: '/learn',
                      element: participantElement('participant.learn', <LearnPage />),
                    },
                    {
                      path: '/learn/onboard',
                      element: participantElement('participant.learn', <LearnOnboardPage />),
                    },
                    {
                      path: '/learn/profile',
                      element: participantElement('participant.learn', <LearnProfilePage />),
                    },
                    {
                      path: '/learn/books',
                      element: participantElement('participant.learn', <LearnBooksPage />),
                    },
                    {
                      path: '/learn/books/:bookId',
                      element: participantElement('participant.learn', <LearnBookPage />),
                    },
                    {
                      path: '/learn/stats',
                      element: participantElement('participant.learn', <LearnStatsPage />),
                    },
                    {
                      path: '/learn/resources',
                      element: participantElement('participant.learn', <LearnResourcesPage />),
                    },
                    {
                      path: '/learn/:topicId',
                      element: participantElement('participant.learn', <LearnTopicPage />),
                    },
                    {
                      path: '/learn/:topicId/:moduleId',
                      element: participantElement('participant.learn', <LearnModulePage />),
                    },
                  ]
                : []),
            ],
          },
        ],
      },
    ],
  },
  // Standalone legacy benefits topic page — own layout, no sidebar or chat
  // panel. Moved off /benefits/:topicId 2026-09-02 -- that path now belongs
  // to the new Exit Guide-backed detail page below. Kept live per
  // instruction, just no longer the page /benefits/:something resolves to.
  ...(enableSupport
    ? [
        {
          element: <ProtectedRoute />,
          errorElement: <ErrorBoundary />,
          children: [
            {
              element: <SupportStandaloneLayout logoHref="/benefits-legacy" />,
              children: [
                {
                  path: '/benefits-legacy/:topicId',
                  element: participantElement('participant.benefits', <SupportTopicPage />),
                },
                {
                  path: '/support/:topicId',
                  element: participantElement(
                    'participant.benefits',
                    <LegacySupportTopicRedirect />,
                  ),
                },
              ],
            },
          ],
        },
      ]
    : []),
  // Legal and public pages without MainLayout header (show just "Pelgo")
  {
    path: '/sms-opt-in-example',
    element: <SMSOptInExamplePage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
    errorElement: <ErrorBoundary />,
  },
  ...(enableBenefits
    ? [
        {
          // Public claim/verify entry. ?org=<slug> identifies the cohort.
          path: '/benefits',
          element: <BenefitsSignupPage />,
          errorElement: <ErrorBoundary />,
        },
      ]
    : []),
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
])

// pelgo.com/benefits is a vanity entry — the flow lives in the embrace app.
// Redirect to the embrace subdomain of whatever host we're on (preserving
// ?org=), so pelgo.com → embrace.pelgo.com and the staging apex maps too.
function BenefitsRedirect() {
  if (typeof window !== 'undefined') {
    const { hostname, search, protocol } = window.location
    const target = hostname.startsWith('embrace.')
      ? `/benefits${search}`
      : `${protocol}//embrace.${hostname}/benefits${search}`
    window.location.replace(target)
  }
  return null
}

// Pelgo (landing page) routes
const pelgoRouter = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
    errorElement: <ErrorBoundary />,
  },
  // Dev-only scenario demo for resume-builder scoping (no auth, standalone).
  ...(import.meta.env.DEV
    ? [
        {
          path: '/resume-builder-poc',
          element: <ResumeBuilderPocPage />,
          errorElement: <ErrorBoundary />,
        },
      ]
    : []),
  {
    path: '/severance',
    element: <SeverancePage />,
    errorElement: <ErrorBoundary />,
  },
  ...(enableBenefits
    ? [
        {
          path: '/benefits',
          element: <BenefitsRedirect />,
          errorElement: <ErrorBoundary />,
        },
      ]
    : []),
  {
    path: '/contact',
    element: <ContactPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/status',
    element: <StatusPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/updates',
    element: <UpdatesPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/news',
    element: <NewsIndexPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/news/:slug',
    element: <NewsArticlePage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/severance-administration-playbook',
    element: <SeveranceAdministrationPlaybookPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resources/whitepapers',
    element: <WhitepapersIndexPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resources/whitepapers/:slug',
    element: <WhitepaperLandingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resources/whitepapers/:slug/download',
    element: <WhitepaperDownloadPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/assets/whitepapers/empathy-imperative-2026.pdf',
    element: <Navigate to="/resources/whitepapers/empathy-imperative-2026" replace />,
  },
  {
    path: '/assets/whitepapers/great-recomposition-2026.pdf',
    element: <Navigate to="/resources/whitepapers/great-recomposition-2026" replace />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
    errorElement: <ErrorBoundary />,
  },
])

const statusRouter = createBrowserRouter([
  {
    path: '/',
    element: <StatusPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
])

// Portal (HR admin) routes
const portalRouter = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login',
    element: <PortalLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <ProtectedPortalRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <PortalLayout />,
        children: [
          {
            path: '/dashboard',
            element: (
              <AccessGuard routeId="portal.root">
                <PortalDashboardPage />
              </AccessGuard>
            ),
          },
          {
            path: '/people',
            element: (
              <AccessGuard routeId="portal.people">
                <PortalEmployeesPage />
              </AccessGuard>
            ),
          },
          {
            path: '/stakeholders',
            element: (
              <AccessGuard routeId="portal.stakeholders.manage">
                <PortalStakeholderContactsPage />
              </AccessGuard>
            ),
          },
          {
            path: '/internal-jobs',
            element: (
              <AccessGuard routeId="portal.internal-jobs">
                <PortalInternalJobsPage />
              </AccessGuard>
            ),
          },
          {
            path: '/internal-mobility',
            element: (
              <AccessGuard routeId="portal.internal-jobs">
                <PortalInternalMobilityDepartmentNeedsPage />
              </AccessGuard>
            ),
          },
          {
            path: '/internal-mobility/department-needs',
            element: <Navigate to="/internal-mobility" replace />,
          },
          {
            path: '/internal-mobility/department-needs/:departmentId',
            element: (
              <AccessGuard routeId="portal.internal-jobs">
                <PortalInternalMobilityDepartmentDetailPage />
              </AccessGuard>
            ),
          },
          {
            path: '/internal-mobility/candidates',
            element: (
              <AccessGuard routeId="portal.internal-jobs">
                <PortalInternalMobilityCandidatesPage />
              </AccessGuard>
            ),
          },
          {
            path: '/internal-mobility/matrix',
            element: (
              <AccessGuard routeId="portal.internal-jobs">
                <PortalInternalMobilityMatrixPage />
              </AccessGuard>
            ),
          },
          {
            path: '/internal-mobility/organization-chart',
            element: (
              <AccessGuard routeId="portal.internal-jobs">
                <PortalInternalMobilityOrganizationChartPage />
              </AccessGuard>
            ),
          },
          ...(enableBenefits
            ? [
                {
                  path: '/benefits',
                  element: (
                    <AccessGuard routeId="portal.severance">
                      <PortalBenefitsPage />
                    </AccessGuard>
                  ),
                },
                {
                  path: '/severance-import',
                  element: (
                    <AccessGuard routeId="portal.severance.manage">
                      <PortalSeveranceImportPage />
                    </AccessGuard>
                  ),
                },
                {
                  path: '/cost-savings',
                  element: enableCostSavings ? (
                    <AccessGuard routeId="portal.severance">
                      <PortalRoiPage />
                    </AccessGuard>
                  ) : (
                    <Navigate to="/benefits" replace />
                  ),
                },
              ]
            : []),
          {
            path: '/hris',
            element: (
              <AccessGuard routeId="portal.root">
                <PortalHrisConnectionPage />
              </AccessGuard>
            ),
          },
          {
            path: '/exit-guide-documents',
            element: (
              <AccessGuard routeId="portal.documents.manage">
                <PortalExitGuideDocumentsPage />
              </AccessGuard>
            ),
          },
          {
            path: '/import',
            element: (
              <AccessGuard routeId="portal.people.manage">
                <PortalImportPage />
              </AccessGuard>
            ),
          },
          {
            path: '/imports',
            element: (
              <AccessGuard routeId="portal.people.manage">
                <PortalImportHistoryPage />
              </AccessGuard>
            ),
          },
          ...(enableBenefits
            ? [
                {
                  path: '/unmatched',
                  element: (
                    <AccessGuard routeId="portal.people.manage">
                      <PortalMatchReportsPage />
                    </AccessGuard>
                  ),
                },
              ]
            : []),
        ],
      },
    ],
  },
])

// Coach Portal routes
const coachRouter = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login',
    element: <CoachLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <ProtectedCoachRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <CoachLayout />,
        children: [
          {
            path: '/dashboard',
            element: <CoachDashboardPage />,
          },
          {
            path: '/clients',
            element: <CoachClientsPage />,
          },
          {
            path: '/clients/:candidateId',
            element: <CoachClientsPage />,
          },
          {
            path: '/profile',
            element: <CoachProfilePage />,
          },
          {
            path: '/availability',
            element: <CoachAvailabilityPage />,
          },
          {
            path: '/session-types',
            element: <Navigate to="/availability" replace />,
          },
          {
            path: '/sessions',
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: '/messages',
            element: <CoachConversationsPage />,
          },
          {
            path: '/group-sessions',
            element: <Navigate to="/dashboard" replace />,
          },
        ],
      },
    ],
  },
])

// Coach Manager Portal routes (Betty/Francisco): coach-domain admin duties
// only, distinct from the full admin console.
const coachManagerRouter = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login',
    element: <CoachManagerLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <ProtectedCoachManagerRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <CoachManagerLayout />,
        children: [
          {
            path: '/dashboard',
            element: <CoachManagerOperationsPage />,
          },
          {
            path: '/coaches',
            element: <CoachManagerCoachesPage />,
          },
          {
            path: '/candidates',
            element: <CoachManagerCandidatesPage />,
          },
          {
            path: '/matching',
            element: <CoachManagerMatchingPage />,
          },
          {
            path: '/reviews',
            element: <CoachManagerReviewsPage />,
          },
          {
            path: '/coach-review',
            element: <CoachManagerReviewPage />,
          },
          {
            path: '/chat-safeguards',
            element: <CoachManagerChatSafeguardsPage />,
          },
        ],
      },
    ],
  },
])

// Select router based on site
function getRouter() {
  switch (site) {
    case 'console':
      return consoleRouter
    case 'coach-manager':
      return coachManagerRouter
    case 'portal':
      return portalRouter
    case 'coach':
      return coachRouter
    case 'status':
      return statusRouter
    case 'pelgo':
      return pelgoRouter
    default:
      return embraceRouter
  }
}

function App() {
  const router = getRouter()
  const routerElement = <RouterProvider router={router} />
  const requiresAuthorization = !['pelgo', 'status'].includes(site)

  return (
    <QueryClientProvider client={queryClient}>
      <AuthSessionBoundary requireAuthorization={requiresAuthorization}>
        {/* Lenis for pelgo marketing routes only; embrace / console / portal use native scroll */}
        {site === 'pelgo' ? <LenisProvider>{routerElement}</LenisProvider> : routerElement}
      </AuthSessionBoundary>
      {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  )
}

export default App
